import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';

import { makePassive } from '@/lib/passive-events';
import { store } from '@/lib/store';
import '@/globals.css';
import '@/lib/i18n';

// Import the generated route tree
import { routeTree } from './routeTree.gen';

// Initialize passive event listeners to improve scroll performance
// This must be called before any components are rendered
makePassive();

// Create a new router
const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router;
	}
}

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: false,
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			refetchOnReconnect: false,
			staleTime: 1000 * 60 * 5, // 5 minutes
		},
	},
});

const rootElement = document.getElementById('root');
if (!rootElement) {
	throw new Error('Root element not found');
}
if (!rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);
	root.render(
		<StrictMode>
			<Provider store={store}>
				<QueryClientProvider client={queryClient}>
					<RouterProvider router={router} />
				</QueryClientProvider>
			</Provider>
		</StrictMode>,
	);
}
